import {
  createAsyncThunk,
  ActionReducerMapBuilder,
  EntityState,
  createAction,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';
import { Filter } from '@vk-hr-tek/core/filter';

import { ContentService } from '@app/gen/content';
import {
  DashboardService,
  GetAccountWidgetTopNewsQuery,
  GetAccountWidgetTopNewsParams,
  UpdateWidgetTopNewsQuery,
  UpdateWidgetTopNewsParams,
  UpdateWidgetTopNewsRequest,
  GetAccountWidgetTopNewsResponse,
} from '@app/gen/dashboard';
import { ThunkExtra } from '@app/store';
import { Entity } from '@app/types';
import { CompanyEventsStat, CompanyGroupUsers } from '@app/gen/settings';
import {
  EditorNewsListResponse,
  NewsService,
  TopNewsListRequest,
} from '@app/gen/news';

import { IMAGE_HEIGHT_MEDIUM, IMAGE_WIDTH_MEDIUM } from '../../../constants';
import { SettingsState, SettingsWithRootState } from '../../../slice';

const setWidgetContentNewsListFilters =
  createAction<Filter[]>('news/setFilters');

const setWidgetContentNewsImages =
  createAction<{ id: string; image: string | null }[]>('news/setImages');

type LoadTopNewsSettingsImagesParams = {
  news: { id: string; cover_att_id: string }[];
};

const loadTopNewsSettingsImages = createAsyncThunk<
  { id: string; image: string | null }[],
  LoadTopNewsSettingsImagesParams,
  ThunkExtra
>(
  'topNewsSettings/loadImages',
  async (params, { rejectWithValue, extra: { inject }, dispatch }) => {
    try {
      const contentService = inject(ContentService);
      const imagesPromises = params.news.map(async (newsItem) => {
        const imageBlob = await contentService.resizeImage({
          query: {
            height: IMAGE_HEIGHT_MEDIUM,
            width: IMAGE_WIDTH_MEDIUM,
          },
          params: {
            attachment_id: newsItem.cover_att_id,
          },
        });

        return {
          image: URL.createObjectURL(await imageBlob.file),
          id: newsItem.id,
        };
      });

      const imagesSettled = await Promise.allSettled(imagesPromises);

      const images = imagesSettled.map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value;
        }

        return {
          id: params.news[index].id,
          image: null,
        };
      });

      dispatch(setWidgetContentNewsImages(images));
      return images;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getTopNewsSettings = createAsyncThunk<
  GetAccountWidgetTopNewsResponse,
  {
    skipImages?: boolean;
    values: {
      query?: GetAccountWidgetTopNewsQuery;
      params: GetAccountWidgetTopNewsParams;
    };
  },
  ThunkExtra
>(
  'topNewsSettings/getTopNewsSettings',
  async (
    { values, skipImages },
    { rejectWithValue, extra: { inject }, dispatch },
  ) => {
    try {
      const service = inject(DashboardService);

      const response = await service.getAccountWidgetTopNews({ ...values });

      if (!skipImages && response.news.length > 0) {
        dispatch(
          loadTopNewsSettingsImages({
            news: response.news.map(({ id, cover_att_id }) => ({
              id,
              cover_att_id,
            })),
          }),
        );
      }

      return response;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateTopNewsSettings = createAsyncThunk<
  void,
  {
    values: {
      body: UpdateWidgetTopNewsRequest;
      params: UpdateWidgetTopNewsParams;
      query?: UpdateWidgetTopNewsQuery;
    };
    actions: {
      resolve: () => void;
    };
  },
  ThunkExtra
>(
  'topNewsSettings/updateTopNewsSettings',
  async (
    { values, actions },
    { rejectWithValue, extra: { inject }, dispatch },
  ) => {
    try {
      const service = inject(DashboardService);
      await service.updateWidgetTopNews({ ...values });

      actions.resolve();
      dispatch(
        showCustomNotification([
          {
            title: 'Виджет успешно изменен',
            message: 'Главные новости',
            type: 'info',
          },
        ]),
      );
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            title: 'Не удалось сохранить виджет',
            message: 'Попробуйте еще раз позже',
            type: 'error',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getNewsList = createAsyncThunk<
  EditorNewsListResponse,
  TopNewsListRequest,
  ThunkExtra<SettingsWithRootState>
>(
  'widgetsContent/news/getList',
  async (dto, { rejectWithValue, dispatch, getState, extra: { inject } }) => {
    try {
      const service = inject(NewsService);
      const { list } = getState().settings.topNewsDetail;

      let filters = list.filters;

      if (!filters) {
        const response = await service.getAvailableNewsEditorFilters();
        filters = response.filters;

        dispatch(setWidgetContentNewsListFilters(filters));
      }

      const response = await service.getTopNewsList({ body: dto });

      if (response.news_list.length > 0) {
        await dispatch(
          loadTopNewsSettingsImages({
            news: response.news_list.map(({ id, cover_att_id }) => ({
              id,
              cover_att_id,
            })),
          }),
        );
      }

      return response;
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsWidgetTopNewsReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getTopNewsSettings.pending, (state) => {
    state.topNewsDetail.settings.status = 'loading';
  });

  builder.addCase(getTopNewsSettings.fulfilled, (state, { payload }) => {
    state.topNewsDetail.settings.status = 'complete';
    state.topNewsDetail.settings.items = payload.news;
    state.topNewsDetail.settings.type = payload.type;
    state.topNewsDetail.settings.widgetSize = payload.widget_size;
    state.topNewsDetail.settings.isException = !!payload.company_content_id;
    state.topNewsDetail.settings.hasParent = !!payload.source_widget_id;
  });

  builder.addCase(getTopNewsSettings.rejected, (state, { payload, error }) => {
    state.topNewsDetail.settings.status = 'failed';
    state.topNewsDetail.settings.error =
      payload ||
      ({
        info: (error && error.message) || 'Что-то пошло не так',
        status: 500,
        source: 'client',
        title: 'Internal client error',
      } as AppError);
  });
  builder.addCase(getNewsList.pending, (state) => {
    state.topNewsDetail.list.items = [];
    state.topNewsDetail.list.status = 'loading';
    state.topNewsDetail.list.total = 0;
    state.topNewsDetail.list.totalPublished = 0;
  });
  builder.addCase(getNewsList.fulfilled, (state, { payload }) => {
    state.topNewsDetail.list.items = payload.news_list;
    state.topNewsDetail.list.status = 'complete';
    state.topNewsDetail.list.total = payload.total;
    state.topNewsDetail.list.totalPublished = payload.total_published ?? 0;
  });
  builder.addCase(getNewsList.rejected, (state) => {
    state.topNewsDetail.list.items = [];
    state.topNewsDetail.list.status = 'failed';
    state.topNewsDetail.list.total = 0;
    state.topNewsDetail.list.totalPublished = 0;
  });
  builder.addCase(setWidgetContentNewsListFilters, (state, { payload }) => {
    state.topNewsDetail.list.filters = payload;
  });
  builder.addCase(setWidgetContentNewsImages, (state, { payload }) => {
    state.topNewsDetail.list.images = [
      ...state.topNewsDetail.list.images,
      ...payload,
    ];
  });
};
